import type {
  CaptionPayload,
  RecordingInfo,
  RecordingLevelPayload,
  RecordingStatePayload,
} from '@/lib/utils/tauri-recording'
import { create } from 'zustand'

export interface LinkedModuleSelection {
  moduleType: string
  moduleId: string
  moduleName: string
}

// ===== Scratchpad persistence (crash-safe, keyed by local recording id) =====
// The in-meeting scratchpad survives window reloads and app crashes; entries
// are removed once the text reaches the API (or the recording is discarded).

const SCRATCHPAD_STORAGE_KEY = 'scrambl:recording-scratchpads'

function loadScratchpads(): Record<string, string> {
  try {
    return JSON.parse(
      localStorage.getItem(SCRATCHPAD_STORAGE_KEY) ?? '{}',
    ) as Record<string, string>
  } catch {
    return {}
  }
}

function persistScratchpad(recordingId: string, text: string): void {
  const all = loadScratchpads()
  if (text) {
    all[recordingId] = text
  } else {
    delete all[recordingId]
  }
  try {
    localStorage.setItem(SCRATCHPAD_STORAGE_KEY, JSON.stringify(all))
  } catch {
    /* localStorage unavailable — in-memory state still covers the session */
  }
}

export function getStoredScratchpad(recordingId: string): string {
  return loadScratchpads()[recordingId] ?? ''
}

// Where the note goes and how it's summarized, kept beside the scratchpad so
// a recording continued after an app quit/crash ("Keep recording") picks up
// the same choices.
const CONTEXT_STORAGE_KEY = 'scrambl:recording-contexts'

interface StoredRecordingContext {
  linkedModule: LinkedModuleSelection | null
  summaryTemplate: string | null
}

function loadContexts(): Record<string, StoredRecordingContext> {
  try {
    return JSON.parse(
      localStorage.getItem(CONTEXT_STORAGE_KEY) ?? '{}',
    ) as Record<string, StoredRecordingContext>
  } catch {
    return {}
  }
}

function persistContext(
  recordingId: string,
  context: StoredRecordingContext | null,
): void {
  const all = loadContexts()
  if (context && (context.linkedModule || context.summaryTemplate)) {
    all[recordingId] = context
  } else {
    delete all[recordingId]
  }
  try {
    localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(all))
  } catch {
    /* localStorage unavailable — in-memory state still covers the session */
  }
}

export function getStoredRecordingContext(
  recordingId: string,
): StoredRecordingContext | null {
  return loadContexts()[recordingId] ?? null
}

export type CaptionsActiveState = boolean | 'starting' | null

// ===== Live captions preference (persists across sessions) =====
// Single source of truth read by RecordButton (checkbox) AND the meeting
// reminder controller, so calendar/ad-hoc-started recordings caption too.

const LIVE_CAPTIONS_PREF_KEY = 'scrambl:live-captions-enabled'

export function isLiveCaptionsPreferred(): boolean {
  try {
    return localStorage.getItem(LIVE_CAPTIONS_PREF_KEY) === 'true'
  } catch {
    return false
  }
}

export function setLiveCaptionsPreferred(enabled: boolean): void {
  try {
    localStorage.setItem(LIVE_CAPTIONS_PREF_KEY, String(enabled))
  } catch {
    /* localStorage unavailable — session-only preference */
  }
}

/** Drop a recording's locally kept notes and note settings (uploaded or
 *  discarded). */
export function clearStoredScratchpad(recordingId: string): void {
  persistScratchpad(recordingId, '')
  persistContext(recordingId, null)
}

interface RecordingStoreState {
  // Mirrors Rust state (Rust is the source of truth; windows are ephemeral)
  state: 'idle' | 'recording' | 'paused' | 'finalizing' | string
  recordingId: string | null
  startedAt: string | null
  durationMs: number
  levels: RecordingLevelPayload
  // Module the generated note will be tied to (null → private My Note)
  linkedModule: LinkedModuleSelection | null
  // Summary template ref (builtin key | 'custom:<uuid>'; null → general)
  summaryTemplate: string | null
  // External calendar event/series key when this recording was started from a
  // meeting prompt — template changes get saved as that event's default
  activeEventKey: string | null
  // In-meeting scratchpad text (merged into the AI summary at stop)
  scratchpad: string
  // Live on-device captions for the active session (desktop, whisper.cpp)
  captions: CaptionPayload[]
  // Caption engine state this session: null = not requested, 'starting' =
  // requested and loading (mic warm-up + whisper model), true = running,
  // false = requested but won't run (model absent / engine error)
  captionsActive: CaptionsActiveState
  // Right-side live recording panel (auto-opens when a recording starts)
  isLivePanelOpen: boolean
  // Session controls registered by RecordButton (which owns the web recorder
  // instance) so the panel can drive the same handlers
  controls: {
    pause: () => void
    resume: () => void
    stop: () => Promise<void>
    discard: () => Promise<void>
  } | null
  // Local recordings awaiting upload (orphans from crashes/restarts)
  pendingUploads: RecordingInfo[]
  uploadProgress: Record<string, { uploadedBytes: number; totalBytes: number }>
  // Recording ids the user discarded — the auto-uploader must skip these so a
  // discarded recording is never uploaded once it lands on disk as "recorded".
  discardedIds: string[]
  // Whether system audio (other participants) is being captured this session.
  // null = unknown/not started; false = mic-only (e.g. permission off).
  systemAudioCaptured: boolean | null
  lastError: { code: string; message: string } | null

  applyStatePayload: (payload: RecordingStatePayload) => void
  setLevels: (levels: RecordingLevelPayload) => void
  setSystemAudioCaptured: (captured: boolean | null) => void
  setLinkedModule: (module: LinkedModuleSelection | null) => void
  setSummaryTemplate: (templateRef: string | null) => void
  setActiveEventKey: (eventKey: string | null) => void
  setScratchpad: (text: string) => void
  appendCaption: (caption: CaptionPayload) => void
  setCaptionsActive: (active: CaptionsActiveState) => void
  setLivePanelOpen: (open: boolean) => void
  registerControls: (controls: RecordingStoreState['controls']) => void
  markDiscarded: (id: string) => void
  isDiscarded: (id: string) => boolean
  clearDiscarded: (id: string) => void
  setPendingUploads: (recordings: RecordingInfo[]) => void
  removePendingUpload: (id: string) => void
  setUploadProgress: (
    id: string,
    progress: { uploadedBytes: number; totalBytes: number },
  ) => void
  setError: (error: { code: string; message: string } | null) => void
  reset: () => void
}

export const useRecordingStore = create<RecordingStoreState>((set, get) => ({
  state: 'idle',
  recordingId: null,
  startedAt: null,
  durationMs: 0,
  levels: { mic: 0, system: 0 },
  linkedModule: null,
  summaryTemplate: null,
  activeEventKey: null,
  scratchpad: '',
  captions: [],
  captionsActive: null,
  isLivePanelOpen: false,
  controls: null,
  pendingUploads: [],
  uploadProgress: {},
  discardedIds: [],
  systemAudioCaptured: null,
  lastError: null,

  applyStatePayload: payload =>
    set(prev => {
      const isActive = payload.state !== 'idle'
      // A recording continued after a restart: restore its note settings.
      const stored =
        isActive &&
        payload.recordingId &&
        payload.recordingId !== prev.recordingId
          ? getStoredRecordingContext(payload.recordingId)
          : null
      const linkedModule = !isActive
        ? null
        : (prev.linkedModule ?? stored?.linkedModule ?? null)
      const summaryTemplate = !isActive
        ? null
        : (prev.summaryTemplate ?? stored?.summaryTemplate ?? null)
      // Choices made in the menu before recording began belong to it now.
      if (isActive && payload.recordingId && !prev.recordingId) {
        persistContext(payload.recordingId, { linkedModule, summaryTemplate })
      }
      return {
        state: payload.state,
        recordingId: payload.recordingId ?? null,
        startedAt: payload.startedAt ?? null,
        durationMs: payload.durationMs,
        // Hydrate the scratchpad after a window reload mid-recording; clear the
        // in-memory copy on idle (persisted text survives until upload/discard)
        scratchpad:
          payload.state === 'idle'
            ? ''
            : prev.scratchpad ||
              (payload.recordingId
                ? getStoredScratchpad(payload.recordingId)
                : ''),
        // Clear the module selection once we return to idle
        linkedModule,
        summaryTemplate,
        activeEventKey: payload.state === 'idle' ? null : prev.activeEventKey,
        captions: payload.state === 'idle' ? [] : prev.captions,
        captionsActive: payload.state === 'idle' ? null : prev.captionsActive,
        // Auto-open the live panel when a recording starts; close on idle
        isLivePanelOpen:
          payload.state === 'idle'
            ? false
            : prev.state === 'idle'
              ? true
              : prev.isLivePanelOpen,
        levels: payload.state === 'idle' ? { mic: 0, system: 0 } : prev.levels,
        systemAudioCaptured:
          payload.state === 'idle' ? null : prev.systemAudioCaptured,
      }
    }),
  setLevels: levels => set({ levels }),
  setSystemAudioCaptured: systemAudioCaptured => set({ systemAudioCaptured }),
  setLinkedModule: linkedModule => {
    const { recordingId, summaryTemplate } = get()
    if (recordingId)
      persistContext(recordingId, { linkedModule, summaryTemplate })
    set({ linkedModule })
  },
  setSummaryTemplate: summaryTemplate => {
    const { recordingId, linkedModule } = get()
    if (recordingId)
      persistContext(recordingId, { linkedModule, summaryTemplate })
    set({ summaryTemplate })
  },
  setActiveEventKey: activeEventKey => set({ activeEventKey }),
  setScratchpad: scratchpad => {
    const { recordingId } = get()
    if (recordingId) persistScratchpad(recordingId, scratchpad)
    set({ scratchpad })
  },
  appendCaption: caption =>
    set(prev => ({
      // A 2h meeting yields ~1-2k lines; cap well above that
      captions: [...prev.captions.slice(-2999), caption],
    })),
  setCaptionsActive: captionsActive => set({ captionsActive }),
  setLivePanelOpen: isLivePanelOpen => set({ isLivePanelOpen }),
  registerControls: controls => set({ controls }),
  markDiscarded: id =>
    set(prev =>
      prev.discardedIds.includes(id)
        ? prev
        : { discardedIds: [...prev.discardedIds, id] },
    ),
  isDiscarded: id => get().discardedIds.includes(id),
  clearDiscarded: id =>
    set(prev => ({
      discardedIds: prev.discardedIds.filter(existing => existing !== id),
    })),
  setPendingUploads: pendingUploads => set({ pendingUploads }),
  removePendingUpload: id =>
    set(prev => ({
      pendingUploads: prev.pendingUploads.filter(r => r.id !== id),
      uploadProgress: Object.fromEntries(
        Object.entries(prev.uploadProgress).filter(([key]) => key !== id),
      ),
    })),
  setUploadProgress: (id, progress) =>
    set(prev => ({
      uploadProgress: { ...prev.uploadProgress, [id]: progress },
    })),
  setError: lastError => set({ lastError }),
  reset: () =>
    set({
      state: 'idle',
      recordingId: null,
      startedAt: null,
      durationMs: 0,
      levels: { mic: 0, system: 0 },
      linkedModule: null,
      summaryTemplate: null,
      activeEventKey: null,
      lastError: null,
    }),
}))
